<template>
  <div>
    <b-modal
      id="privacy-policy-modal"
      title="Privacy Policy"
      size="lg"
      scrollable
      ok-only
      ok-title="Close"
      ok-variant="secondary"
    >
      <div class="privacy-content">
        <p class="text-muted small">
          Last updated: February 2026
        </p>

        <h6>1. Introduction</h6>
        <p>
          Invitial ("we", "us", "our") is committed to protecting the personal data of users of the
          IFE Data platform ("the Service"). This Privacy Policy explains what personal data we
          collect, why we collect it, how we use it, and your rights in relation to it. It applies
          to all authorised users of the Service.
        </p>

        <h6>2. Data Controller</h6>
        <p>
          Invitial acts as the data controller in respect of personal data processed about users of
          the Service. If you have any questions about how we handle your personal data, please
          contact us at <a href="mailto:info@ifedata.com">info@ifedata.com</a>.
        </p>

        <h6>3. Personal Data We Collect</h6>
        <p>We collect and process the following categories of personal data:</p>
        <ul>
          <li>
            <strong>Account information:</strong> your name, email address, job title, and company
            name, provided when your account is created or when you request access;
          </li>
          <li>
            <strong>Authentication data:</strong> encrypted credentials used to verify your identity
            when you log in;
          </li>
          <li>
            <strong>Usage data:</strong> information about how you use the Service, including pages
            visited, actions taken, and timestamps of activity;
          </li>
          <li>
            <strong>Technical data:</strong> IP address, browser type and version, and device
            information collected automatically when you access the Service. This may include session
            cookies and similar technologies used to maintain your authenticated session and ensure
            the security and performance of the Service.
          </li>
        </ul>

        <h6>4. Cookies and Similar Technologies</h6>
        <p>We use cookies and similar technologies to operate the Service. These include:</p>
        <ul>
          <li>
            <strong>Session cookies:</strong> used to maintain your authenticated login session.
            These are deleted when you close your browser.
          </li>
          <li>
            <strong>Security cookies:</strong> used to detect and prevent fraudulent or unauthorised
            activity.
          </li>
        </ul>
        <p>
          We do not use cookies for advertising or tracking purposes beyond what is necessary to
          operate the Service. If you disable cookies, you may not be able to log in or use the
          Service.
        </p>

        <h6>5. How We Use Your Personal Data</h6>
        <p>We use your personal data for the following purposes:</p>
        <ul>
          <li>to provide, operate, and maintain the Service;</li>
          <li>to authenticate your identity and manage your account access;</li>
          <li>to communicate with you about your account, the Service, or any support queries;</li>
          <li>to monitor and improve the security and performance of the Service; and</li>
          <li>to comply with our legal obligations and enforce our Terms of Service.</li>
        </ul>

        <h6>6. Legal Basis for Processing</h6>
        <p>We process your personal data on the following legal bases:</p>
        <ul>
          <li>
            <strong>Contractual necessity:</strong> processing required to provide the Service under
            the agreement with your organisation;
          </li>
          <li>
            <strong>Legitimate interests:</strong> monitoring Service security, preventing fraud, and
            improving the Service;
          </li>
          <li>
            <strong>Legal obligation:</strong> where we are required to process data to comply with
            applicable law.
          </li>
        </ul>

        <h6>7. Data Sharing</h6>
        <p>
          We do not sell your personal data. We may share your personal data with trusted third-party
          service providers who assist us in operating the Service (such as hosting and infrastructure
          providers), where those parties are bound by appropriate data processing agreements. We may
          also disclose your data where required by law or to protect the rights and safety of
          Invitial or others.
        </p>
        <p>
          We do not share your personal data with any third parties for their own marketing or
          commercial purposes.
        </p>

        <h6>8. International Transfers</h6>
        <p>
          Where your personal data is transferred outside the UK or European Economic Area, we ensure
          appropriate safeguards are in place in accordance with applicable data protection law, such
          as standard contractual clauses approved by the relevant regulatory authority.
        </p>

        <h6>9. Data Retention</h6>
        <p>
          We retain your personal data for as long as your account is active or as necessary to
          provide the Service.
        </p>
        <p>
          Following termination or closure of your account, we will retain personal data for a period
          of up to 90 days, after which it will be securely deleted or anonymised. Content data
          entered into the Service by your organisation may be retrieved by written request submitted
          within 30 days of account termination, in accordance with our Terms of Service. After that
          period, content data will be deleted.
        </p>
        <p>
          We may retain certain data for longer periods where required for legal, audit, or security
          purposes. When personal data is no longer needed, it is securely deleted or anonymised.
        </p>

        <h6>10. Security</h6>
        <p>
          We implement appropriate technical and organisational measures to protect your personal data
          against unauthorised access, loss, or disclosure. These include access controls, encryption
          of data in transit, and regular security assessments. However, no method of transmission
          over the internet is completely secure, and we cannot guarantee absolute security.
        </p>

        <h6>11. Your Rights</h6>
        <p>
          Subject to applicable data protection law, you may have the following rights in relation to
          your personal data:
        </p>
        <ul>
          <li><strong>Access:</strong> the right to request a copy of the personal data we hold about you;</li>
          <li><strong>Rectification:</strong> the right to request correction of inaccurate or incomplete data;</li>
          <li>
            <strong>Erasure:</strong> the right to request deletion of your personal data in certain
            circumstances;
          </li>
          <li>
            <strong>Restriction:</strong> the right to request that we restrict processing of your
            data in certain circumstances;
          </li>
          <li>
            <strong>Portability:</strong> the right to receive your data in a structured, commonly
            used format; and
          </li>
          <li><strong>Objection:</strong> the right to object to processing based on legitimate interests.</li>
        </ul>
        <p>
          To exercise any of these rights, please contact us at
          <a href="mailto:info@ifedata.com">info@ifedata.com</a>.
          We will respond to your request within one month as required by applicable law.
        </p>
        <p>
          You also have the right to lodge a complaint with the Information Commissioner's Office
          (ICO), the UK's supervisory authority for data protection. You can contact the ICO at
          <a
            href="https://ico.org.uk"
            target="_blank"
            rel="noopener"
          >ico.org.uk</a>
          or by calling 0303 123 1113.
        </p>

        <h6>12. Changes to This Policy</h6>
        <p>
          We may update this Privacy Policy from time to time. Where changes are material, we will
          provide at least 14 days' written notice (including by email) before the changes take
          effect. Continued use of the Service following the effective date of any changes constitutes
          your acceptance of the revised policy. We encourage you to review this policy periodically.
        </p>

        <h6>13. Contact</h6>
        <p>
          If you have any questions or concerns about this Privacy Policy or how we handle your
          personal data, please contact Invitial at
          <a href="mailto:info@ifedata.com">info@ifedata.com</a>.
        </p>
      </div>
    </b-modal>
  </div>
</template>

<script>
export default {
  name: 'PrivacyPolicyModal',
};
</script>

<style scoped>
.privacy-content h6 {
  font-weight: 600;
  margin-top: 1.25rem;
}
</style>
